import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { uploadBrandingAssetWithRetry } from "@/lib/upload-utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ColorPickerSimple } from "@/components/ui/color-picker-simple";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Color from 'color';
import { cn } from "@/lib/utils";
import { BRANDING_PRESETS } from "@/branding/presets";
import { ChatPreview } from "@/components/branding/ChatPreview";
import { Sun, Moon } from "lucide-react";

// Font options for the selector
// Font options with Google Fonts info
const FONT_OPTIONS = {
  sans: [
    {
      name: "Inter",
      value: "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Inter",
      googleFont: "Inter:wght@300;400;500;600;700"
    },
    {
      name: "Roboto",
      value: "Roboto, ui-sans-serif, system-ui, -apple-system, Segoe UI, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Roboto",
      googleFont: "Roboto:wght@300;400;500;700"
    },
    {
      name: "Poppins",
      value: "Poppins, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Poppins",
      googleFont: "Poppins:wght@300;400;500;600;700"
    },
    {
      name: "Open Sans",
      value: "Open Sans, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Open Sans",
      googleFont: "Open+Sans:wght@300;400;500;600;700"
    },
    {
      name: "Lato",
      value: "Lato, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Lato",
      googleFont: "Lato:wght@300;400;700"
    },
    {
      name: "Montserrat",
      value: "Montserrat, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Montserrat",
      googleFont: "Montserrat:wght@300;400;500;600;700"
    },
    {
      name: "Nunito",
      value: "Nunito, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Nunito",
      googleFont: "Nunito:wght@300;400;500;600;700"
    },
    {
      name: "Nunito Sans",
      value: "Nunito Sans, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Nunito Sans",
      googleFont: "Nunito+Sans:wght@300;400;500;600;700"
    },
    {
      name: "Raleway",
      value: "Raleway, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Raleway",
      googleFont: "Raleway:wght@300;400;500;600;700"
    },
    {
      name: "Work Sans",
      value: "Work Sans, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Work Sans",
      googleFont: "Work+Sans:wght@300;400;500;600;700"
    },
    {
      name: "Quicksand",
      value: "Quicksand, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Quicksand",
      googleFont: "Quicksand:wght@300;400;500;600;700"
    },
    {
      name: "Rubik",
      value: "Rubik, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Rubik",
      googleFont: "Rubik:wght@300;400;500;600;700"
    },
    {
      name: "Karla",
      value: "Karla, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Karla",
      googleFont: "Karla:wght@300;400;500;600;700"
    },
    {
      name: "Josefin Sans",
      value: "Josefin Sans, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Josefin Sans",
      googleFont: "Josefin+Sans:wght@300;400;500;600;700"
    },
    {
      name: "Cabin",
      value: "Cabin, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Cabin",
      googleFont: "Cabin:wght@400;500;600;700"
    },
    {
      name: "Ubuntu",
      value: "Ubuntu, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Ubuntu",
      googleFont: "Ubuntu:wght@300;400;500;700"
    },
    {
      name: "Mukta",
      value: "Mukta, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Mukta",
      googleFont: "Mukta:wght@300;400;500;600;700"
    },
    {
      name: "Manrope",
      value: "Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Manrope",
      googleFont: "Manrope:wght@300;400;500;600;700"
    },
    {
      name: "DM Sans",
      value: "DM Sans, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "DM Sans",
      googleFont: "DM+Sans:wght@400;500;700"
    },
    {
      name: "Plus Jakarta Sans",
      value: "Plus Jakarta Sans, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Plus Jakarta Sans",
      googleFont: "Plus+Jakarta+Sans:wght@300;400;500;600;700"
    },
    {
      name: "Space Grotesk",
      value: "Space Grotesk, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Space Grotesk",
      googleFont: "Space+Grotesk:wght@300;400;500;600;700"
    },
    {
      name: "Outfit",
      value: "Outfit, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Outfit",
      googleFont: "Outfit:wght@300;400;500;600;700"
    },
    {
      name: "Figtree",
      value: "Figtree, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Figtree",
      googleFont: "Figtree:wght@300;400;500;600;700"
    },
    {
      name: "Lexend",
      value: "Lexend, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Lexend",
      googleFont: "Lexend:wght@300;400;500;600;700"
    },
    {
      name: "Sora",
      value: "Sora, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "Sora",
      googleFont: "Sora:wght@300;400;500;600;700"
    },
    {
      name: "System UI",
      value: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
      preview: "System UI"
      // No googleFont = system font
    }
  ],
  serif: [
    {
      name: "Merriweather",
      value: "Merriweather, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Merriweather",
      googleFont: "Merriweather:wght@300;400;700"
    },
    {
      name: "Playfair Display",
      value: "Playfair Display, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Playfair Display",
      googleFont: "Playfair+Display:wght@400;500;600;700"
    },
    {
      name: "Source Serif Pro",
      value: "Source Serif Pro, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Source Serif Pro",
      googleFont: "Source+Serif+Pro:wght@400;600"
    },
    {
      name: "Crimson Text",
      value: "Crimson Text, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Crimson Text",
      googleFont: "Crimson+Text:wght@400;600"
    },
    {
      name: "Lora",
      value: "Lora, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Lora",
      googleFont: "Lora:wght@400;500;600;700"
    },
    {
      name: "PT Serif",
      value: "PT Serif, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "PT Serif",
      googleFont: "PT+Serif:wght@400;700"
    },
    {
      name: "Libre Baskerville",
      value: "Libre Baskerville, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Libre Baskerville",
      googleFont: "Libre+Baskerville:wght@400;700"
    },
    {
      name: "EB Garamond",
      value: "EB Garamond, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "EB Garamond",
      googleFont: "EB+Garamond:wght@400;500;600;700"
    },
    {
      name: "Cormorant Garamond",
      value: "Cormorant Garamond, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Cormorant Garamond",
      googleFont: "Cormorant+Garamond:wght@400;500;600;700"
    },
    {
      name: "Bitter",
      value: "Bitter, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Bitter",
      googleFont: "Bitter:wght@400;500;600;700"
    },
    {
      name: "Noto Serif",
      value: "Noto Serif, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Noto Serif",
      googleFont: "Noto+Serif:wght@400;500;600;700"
    },
    {
      name: "DM Serif Display",
      value: "DM Serif Display, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "DM Serif Display",
      googleFont: "DM+Serif+Display:wght@400"
    },
    {
      name: "Fraunces",
      value: "Fraunces, ui-serif, Georgia, Cambria, Times New Roman, Times, serif",
      preview: "Fraunces",
      googleFont: "Fraunces:wght@400;500;600;700"
    },
    {
      name: "Georgia",
      value: "Georgia, ui-serif, Cambria, Times New Roman, Times, serif",
      preview: "Georgia"
      // No googleFont = system font
    },
    {
      name: "Times New Roman",
      value: "Times New Roman, ui-serif, Georgia, Cambria, Times, serif",
      preview: "Times New Roman"
      // No googleFont = system font
    }
  ],
  mono: [
    {
      name: "JetBrains Mono",
      value: "JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "JetBrains Mono",
      googleFont: "JetBrains+Mono:wght@400;500;700"
    },
    {
      name: "Fira Code",
      value: "Fira Code, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Fira Code",
      googleFont: "Fira+Code:wght@300;400;500"
    },
    {
      name: "Source Code Pro",
      value: "Source Code Pro, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Source Code Pro",
      googleFont: "Source+Code+Pro:wght@400;500;600"
    },
    {
      name: "Inconsolata",
      value: "Inconsolata, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Inconsolata",
      googleFont: "Inconsolata:wght@400;500;600;700"
    },
    {
      name: "IBM Plex Mono",
      value: "IBM Plex Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "IBM Plex Mono",
      googleFont: "IBM+Plex+Mono:wght@400;500;600"
    },
    {
      name: "Roboto Mono",
      value: "Roboto Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Roboto Mono",
      googleFont: "Roboto+Mono:wght@400;500;600;700"
    },
    {
      name: "Space Mono",
      value: "Space Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Space Mono",
      googleFont: "Space+Mono:wght@400;700"
    },
    {
      name: "Ubuntu Mono",
      value: "Ubuntu Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Ubuntu Mono",
      googleFont: "Ubuntu+Mono:wght@400;700"
    },
    {
      name: "Anonymous Pro",
      value: "Anonymous Pro, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Anonymous Pro",
      googleFont: "Anonymous+Pro:wght@400;700"
    },
    {
      name: "Red Hat Mono",
      value: "Red Hat Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Red Hat Mono",
      googleFont: "Red+Hat+Mono:wght@400;500;600;700"
    },
    {
      name: "Overpass Mono",
      value: "Overpass Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Overpass Mono",
      googleFont: "Overpass+Mono:wght@400;500;600;700"
    },
    {
      name: "Cousine",
      value: "Cousine, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Cousine",
      googleFont: "Cousine:wght@400;700"
    },
    {
      name: "Monaco",
      value: "Monaco, ui-monospace, SFMono-Regular, Menlo, Consolas, Liberation Mono, Courier New, monospace",
      preview: "Monaco"
      // No googleFont = system font
    },
    {
      name: "Consolas",
      value: "Consolas, ui-monospace, SFMono-Regular, Menlo, Monaco, Liberation Mono, Courier New, monospace",
      preview: "Consolas"
      // No googleFont = system font
    },
    {
      name: "SF Mono",
      value: "SFMono-Regular, ui-monospace, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
      preview: "SF Mono"
      // No googleFont = system font
    }
  ]
};

// Font loading utility
const loadGoogleFont = (googleFontParam: string) => {
  const fontId = `google-font-${googleFontParam.replace(/[^a-zA-Z0-9]/g, '-')}`;
  
  // Check if font is already loaded
  if (document.getElementById(fontId)) {
    return;
  }
  
  // Create and add font link
  const link = document.createElement('link');
  link.id = fontId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${googleFontParam}&display=swap`;
  document.head.appendChild(link);
};

const schema = z.object({
  app_title: z.string().optional(),
  logo_light_url: z.string().optional(),
  logo_dark_url: z.string().optional(),
  sidebar_logo_url: z.string().optional(),
  sidebar_logo_enabled: z.boolean().optional(),
  sidebar_logo_size: z.number().min(20).max(100).optional(),
  favicon_url: z.string().optional(),
    motd: z.string().optional(),
    pro_tip: z.string().optional(),
    motd_enabled: z.boolean().optional(),
    pro_tip_enabled: z.boolean().optional(),

  // Font variables
  font_sans: z.string().optional(),
  font_serif: z.string().optional(),
  font_mono: z.string().optional(),
  // Light theme variables
  primary: z.string().optional(),
  primary_foreground: z.string().optional(),
  secondary: z.string().optional(),
  secondary_foreground: z.string().optional(),
  accent: z.string().optional(),
  accent_foreground: z.string().optional(),
  background: z.string().optional(),
  foreground: z.string().optional(),
  card: z.string().optional(),
  card_foreground: z.string().optional(),
  popover: z.string().optional(),
  popover_foreground: z.string().optional(),
  muted: z.string().optional(),
  muted_foreground: z.string().optional(),
  destructive: z.string().optional(),
  destructive_foreground: z.string().optional(),
  border: z.string().optional(),
  input: z.string().optional(),
  ring: z.string().optional(),
  radius: z.string().optional(),
  chart_1: z.string().optional(),
  chart_2: z.string().optional(),
  chart_3: z.string().optional(),
  chart_4: z.string().optional(),
  chart_5: z.string().optional(),
  sidebar: z.string().optional(),
  sidebar_background: z.string().optional(),
  sidebar_foreground: z.string().optional(),
  sidebar_primary: z.string().optional(),
  sidebar_primary_foreground: z.string().optional(),
  sidebar_accent: z.string().optional(),
  sidebar_accent_foreground: z.string().optional(),
  sidebar_border: z.string().optional(),
  sidebar_ring: z.string().optional(),
  // Dark theme overrides
  dark_primary: z.string().optional(),
  dark_primary_foreground: z.string().optional(),
  dark_secondary: z.string().optional(),
  dark_secondary_foreground: z.string().optional(),
  dark_accent: z.string().optional(),
  dark_accent_foreground: z.string().optional(),
  dark_background: z.string().optional(),
  dark_foreground: z.string().optional(),
  dark_card: z.string().optional(),
  dark_card_foreground: z.string().optional(),
  dark_popover: z.string().optional(),
  dark_popover_foreground: z.string().optional(),
  dark_muted: z.string().optional(),
  dark_muted_foreground: z.string().optional(),
  dark_destructive: z.string().optional(),
  dark_destructive_foreground: z.string().optional(),
  dark_border: z.string().optional(),
  dark_input: z.string().optional(),
  dark_ring: z.string().optional(),
  dark_chart_1: z.string().optional(),
  dark_chart_2: z.string().optional(),
  dark_chart_3: z.string().optional(),
  dark_chart_4: z.string().optional(),
  dark_chart_5: z.string().optional(),
  dark_sidebar: z.string().optional(),
  dark_sidebar_background: z.string().optional(),
  dark_sidebar_foreground: z.string().optional(),
  dark_sidebar_primary: z.string().optional(),
  dark_sidebar_primary_foreground: z.string().optional(),
  dark_sidebar_accent: z.string().optional(),
  dark_sidebar_accent_foreground: z.string().optional(),
  dark_sidebar_border: z.string().optional(),
  dark_sidebar_ring: z.string().optional(),
  // Custom CSS
  custom_css: z.string().max(50000).optional(),
});

type BrandingSettingsData = z.infer<typeof schema>;

type Props = {
  data?: Partial<BrandingSettingsData>;
  onSubmit?: (data: BrandingSettingsData) => void;
  hideSubmitButton?: boolean;
};

const BrandingSettingsForm = forwardRef<any, Props>(({ data, onSubmit, hideSubmitButton }, ref) => {
  const form = useForm<BrandingSettingsData>({
    resolver: zodResolver(schema),
    defaultValues: {
      app_title: data?.app_title || "",
      logo_light_url: data?.logo_light_url || "",
      logo_dark_url: data?.logo_dark_url || "",
      sidebar_logo_url: data?.sidebar_logo_url || "",
      sidebar_logo_enabled: data?.sidebar_logo_enabled ?? true,
      sidebar_logo_size: data?.sidebar_logo_size ?? 40,
      favicon_url: data?.favicon_url || "",
      motd: (data as any)?.motd || "",
      pro_tip: (data as any)?.pro_tip || "",
      motd_enabled: (data as any)?.motd_enabled ?? true,
      pro_tip_enabled: (data as any)?.pro_tip_enabled ?? true,

      // Font variables
      font_sans: data?.font_sans || "",
      font_serif: data?.font_serif || "",
      font_mono: data?.font_mono || "",
      // Light theme variables
      primary: data?.primary || "",
      primary_foreground: data?.primary_foreground || "",
      secondary: data?.secondary || "",
      secondary_foreground: data?.secondary_foreground || "",
      accent: data?.accent || "",
      accent_foreground: data?.accent_foreground || "",
      background: data?.background || "",
      foreground: data?.foreground || "",
      card: data?.card || "",
      card_foreground: data?.card_foreground || "",
      popover: data?.popover || "",
      popover_foreground: data?.popover_foreground || "",
      muted: data?.muted || "",
      muted_foreground: data?.muted_foreground || "",
      destructive: data?.destructive || "",
      destructive_foreground: data?.destructive_foreground || "",
      border: data?.border || "",
      input: data?.input || "",
      ring: data?.ring || "",
      radius: data?.radius || "",
      chart_1: data?.chart_1 || "",
      chart_2: data?.chart_2 || "",
      chart_3: data?.chart_3 || "",
      chart_4: data?.chart_4 || "",
      chart_5: data?.chart_5 || "",
      sidebar: data?.sidebar || "",
      sidebar_background: data?.sidebar_background || "",
      sidebar_foreground: data?.sidebar_foreground || "",
      sidebar_primary: data?.sidebar_primary || "",
      sidebar_primary_foreground: data?.sidebar_primary_foreground || "",
      sidebar_accent: data?.sidebar_accent || "",
      sidebar_accent_foreground: data?.sidebar_accent_foreground || "",
      sidebar_border: data?.sidebar_border || "",
      sidebar_ring: data?.sidebar_ring || "",
      // Dark theme overrides
      dark_primary: data?.dark_primary || "",
      dark_primary_foreground: data?.dark_primary_foreground || "",
      dark_secondary: data?.dark_secondary || "",
      dark_secondary_foreground: data?.dark_secondary_foreground || "",
      dark_accent: data?.dark_accent || "",
      dark_accent_foreground: data?.dark_accent_foreground || "",
      dark_background: data?.dark_background || "",
      dark_foreground: data?.dark_foreground || "",
      dark_card: data?.dark_card || "",
      dark_card_foreground: data?.dark_card_foreground || "",
      dark_popover: data?.dark_popover || "",
      dark_popover_foreground: data?.dark_popover_foreground || "",
      dark_muted: data?.dark_muted || "",
      dark_muted_foreground: data?.dark_muted_foreground || "",
      dark_destructive: data?.dark_destructive || "",
      dark_destructive_foreground: data?.dark_destructive_foreground || "",
      dark_border: data?.dark_border || "",
      dark_input: data?.dark_input || "",
      dark_ring: data?.dark_ring || "",
      dark_chart_1: data?.dark_chart_1 || "",
      dark_chart_2: data?.dark_chart_2 || "",
      dark_chart_3: data?.dark_chart_3 || "",
      dark_chart_4: data?.dark_chart_4 || "",
      dark_chart_5: data?.dark_chart_5 || "",
      dark_sidebar: data?.dark_sidebar || "",
      dark_sidebar_background: data?.dark_sidebar_background || "",
      dark_sidebar_foreground: data?.dark_sidebar_foreground || "",
      dark_sidebar_primary: data?.dark_sidebar_primary || "",
      dark_sidebar_primary_foreground: data?.dark_sidebar_primary_foreground || "",
      dark_sidebar_accent: data?.dark_sidebar_accent || "",
      dark_sidebar_accent_foreground: data?.dark_sidebar_accent_foreground || "",
      dark_sidebar_border: data?.dark_sidebar_border || "",
      dark_sidebar_ring: data?.dark_sidebar_ring || "",
      // Custom CSS
      custom_css: data?.custom_css || "",
    },
  });

  // Watch form values for live preview (without applying to the actual theme)
  const watchedValues = useWatch({ control: form.control });
  
  // Preview mode toggle (light/dark)
  const [previewDark, setPreviewDark] = useState(false);

  const FontField = ({ name, type }: { name: keyof BrandingSettingsData; type: 'sans' | 'serif' | 'mono' }) => {
    const options = FONT_OPTIONS[type];
    
    // Load Google Fonts when component mounts and when value changes
    useEffect(() => {
      // Load fonts for all options on mount (for preview)
      options.forEach(font => {
        if (font.googleFont) {
          loadGoogleFont(font.googleFont);
        }
      });
    }, [options]);
    
    return (
      <FormField
        control={form.control}
        name={name as any}
        render={({ field }) => {
          // Get the effective value (use first option as default if empty)
          const effectiveValue = field.value || options[0].value;
          const currentOption = options.find(opt => opt.value === effectiveValue);
          const currentFontName = currentOption?.name || options[0].name;

          // Load Google Font when selection changes
          const handleFontChange = (value: string) => {
            field.onChange(value);
            
            // Find and load the selected font
            const selectedFont = options.find(opt => opt.value === value);
            if (selectedFont?.googleFont) {
              loadGoogleFont(selectedFont.googleFont);
            }
          };

          return (
            <FormItem>
              <FormLabel className="capitalize">{type} Font</FormLabel>
              <Select
                value={effectiveValue}
                onValueChange={handleFontChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <span 
                        style={{ fontFamily: currentOption?.preview || options[0].preview }}
                        className="text-base"
                      >
                        Aa
                      </span>
                      <span>{currentFontName}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {options.map((font) => (
                    <SelectItem key={font.name} value={font.value}>
                      <div className="flex items-center gap-3">
                        <span 
                          style={{ fontFamily: font.preview }} 
                          className="text-base w-6"
                        >
                          Aa
                        </span>
                        <span>{font.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    );
  };

  const ColorField = ({ name, label }: { name: keyof BrandingSettingsData; label: string }) => {
  const [open, setOpen] = useState(false);
  const [tempValue, setTempValue] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setTempValue(''); // Reset temp value when closing without saving
      }
    };
    
    // Add listener with a small delay to avoid closing on the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setTempValue(''); // Reset temp value when closing without saving
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <FormField
      control={form.control}
      name={name as any}
      render={({ field }) => {
        // Get default value from Slate preset if field is empty
        const getDefaultValue = (): string => {
          const slatePreset = BRANDING_PRESETS.find(p => p.id === 'slate');
          if (!slatePreset) {
            console.warn(`Slate preset not found for ${name}`);
            return '0 0% 50%';
          }
          
          // Check if it's a dark theme field
          const isDark = name.startsWith('dark_');
          const fieldName = isDark ? name.substring(5) : name; // Remove 'dark_' prefix
          
          const colorMap = isDark ? slatePreset.dark : slatePreset.light;
          const defaultValue = colorMap?.[fieldName];
          
          if (!defaultValue) {
            console.warn(`No default value found for ${name} (fieldName: ${fieldName}) in ${isDark ? 'dark' : 'light'} theme`);
            // Fallback to a reasonable gray color
            return '220 13% 69%';
          }
          
          return defaultValue;
        };
      
        // Simple swatch color logic - directly compute from field value
        const getSwatchHex = (): string => {
          // Use field value or default value from preset
          let valueToConvert = field.value;
          if (!valueToConvert || valueToConvert === 'undefined' || valueToConvert.trim() === '') {
            valueToConvert = getDefaultValue();
          }
          
          try {
            const parts = valueToConvert.trim().split(/\s+/);
            if (parts.length === 3) {
              const h = parseFloat(parts[0]);
              const s = parseFloat(parts[1].replace('%', ''));
              const l = parseFloat(parts[2].replace('%', ''));
              
              if (!isNaN(h) && !isNaN(s) && !isNaN(l)) {
                return Color.hsl(h, s, l).hex();
              }
            }
          } catch (error) {
            // Silent fail
          }
          
          // Gray fallback for invalid values
          return '#808080';
        };

        const swatchStyle: React.CSSProperties = {
          backgroundColor: getSwatchHex(),
        };

          return (
            <FormItem>
              <FormLabel className="capitalize">{label}</FormLabel>
              <div className="flex items-center gap-2">
                <div className="relative" ref={containerRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!open) {
                        // Initialize temp value when opening
                        setTempValue(field.value && field.value !== 'undefined' && field.value.trim() !== '' 
                          ? field.value 
                          : getDefaultValue());
                      }
                      setOpen(!open);
                    }}
                    className={cn(
                      "h-9 w-9 rounded-md border border-input shadow-sm transition-colors",
                      "hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                      open && "ring-2 ring-ring ring-offset-2"
                    )}
                    style={swatchStyle}
                    aria-label={`Pick ${label} color`}
                  />
                  
                  {open && (
                    <div 
                      className="absolute z-50 mt-2 rounded-md border bg-popover shadow-md"
                      style={{ minWidth: '320px' }}
                    >
                      <ColorPickerSimple 
                        value={tempValue || (field.value && field.value !== 'undefined' && field.value.trim() !== '' 
                          ? field.value 
                          : getDefaultValue())} 
                        onChange={(hslValue) => {
                          setTempValue(hslValue);
                        }} 
                        className="w-80"
                      />
                      <div className="flex justify-end p-2 border-t">
                        <Button 
                          type="button"
                          size="sm"
                          onClick={() => {
                            if (tempValue) {
                              field.onChange(tempValue);
                            }
                            setOpen(false);
                            setTempValue('');
                          }}
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                
                <Input 
                  className="flex-1" 
                  value={field.value || ""} 
                  onChange={field.onChange} 
                  placeholder="246 72% 65%"
                  aria-label={`${label} HSL value`}
                />
              </div>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    );
  };

  //

  // Reset form when data changes (e.g., after successful save)
  React.useEffect(() => {
    
    if (data) {
      // Build complete form data from database
      const formData: any = {
        // Basic settings
        app_title: data.app_title || "",
        logo_light_url: data.logo_light_url || "",
        logo_dark_url: data.logo_dark_url || "",
        sidebar_logo_url: data.sidebar_logo_url || "",
        sidebar_logo_enabled: data.sidebar_logo_enabled ?? true,
        sidebar_logo_size: data.sidebar_logo_size ?? 40,
        favicon_url: data.favicon_url || "",
        motd: data.motd || "",
        pro_tip: data.pro_tip || "",
        motd_enabled: data.motd_enabled ?? true,
        pro_tip_enabled: data.pro_tip_enabled ?? true,

        // Font variables
        font_sans: data.font_sans || "",
        font_serif: data.font_serif || "",
        font_mono: data.font_mono || "",
        
        // Light theme variables - include ALL fields to ensure proper mapping
        primary: data.primary || "",
        primary_foreground: data.primary_foreground || "",
        secondary: data.secondary || "",
        secondary_foreground: data.secondary_foreground || "",
        accent: data.accent || "",
        accent_foreground: data.accent_foreground || "",
        background: data.background || "",
        foreground: data.foreground || "",
        card: data.card || "",
        card_foreground: data.card_foreground || "",
        popover: data.popover || "",
        popover_foreground: data.popover_foreground || "",
        muted: data.muted || "",
        muted_foreground: data.muted_foreground || "",
        destructive: data.destructive || "",
        destructive_foreground: data.destructive_foreground || "",
        border: data.border || "",
        input: data.input || "",
        ring: data.ring || "",
        radius: data.radius || "",
        chart_1: data.chart_1 || "",
        chart_2: data.chart_2 || "",
        chart_3: data.chart_3 || "",
        chart_4: data.chart_4 || "",
        chart_5: data.chart_5 || "",
        sidebar: data.sidebar || "",
        sidebar_background: data.sidebar_background || "",
        sidebar_foreground: data.sidebar_foreground || "",
        sidebar_primary: data.sidebar_primary || "",
        sidebar_primary_foreground: data.sidebar_primary_foreground || "",
        sidebar_accent: data.sidebar_accent || "",
        sidebar_accent_foreground: data.sidebar_accent_foreground || "",
        sidebar_border: data.sidebar_border || "",
        sidebar_ring: data.sidebar_ring || "",
        
        // Dark theme overrides
        dark_primary: data.dark_primary || "",
        dark_primary_foreground: data.dark_primary_foreground || "",
        dark_secondary: data.dark_secondary || "",
        dark_secondary_foreground: data.dark_secondary_foreground || "",
        dark_accent: data.dark_accent || "",
        dark_accent_foreground: data.dark_accent_foreground || "",
        dark_background: data.dark_background || "",
        dark_foreground: data.dark_foreground || "",
        dark_card: data.dark_card || "",
        dark_card_foreground: data.dark_card_foreground || "",
        dark_popover: data.dark_popover || "",
        dark_popover_foreground: data.dark_popover_foreground || "",
        dark_muted: data.dark_muted || "",
        dark_muted_foreground: data.dark_muted_foreground || "",
        dark_destructive: data.dark_destructive || "",
        dark_destructive_foreground: data.dark_destructive_foreground || "",
        dark_border: data.dark_border || "",
        dark_input: data.dark_input || "",
        dark_ring: data.dark_ring || "",
        dark_chart_1: data.dark_chart_1 || "",
        dark_chart_2: data.dark_chart_2 || "",
        dark_chart_3: data.dark_chart_3 || "",
        dark_chart_4: data.dark_chart_4 || "",
        dark_chart_5: data.dark_chart_5 || "",
        dark_sidebar: data.dark_sidebar || "",
        dark_sidebar_background: data.dark_sidebar_background || "",
        dark_sidebar_foreground: data.dark_sidebar_foreground || "",
        dark_sidebar_primary: data.dark_sidebar_primary || "",
        dark_sidebar_primary_foreground: data.dark_sidebar_primary_foreground || "",
        dark_sidebar_accent: data.dark_sidebar_accent || "",
        dark_sidebar_accent_foreground: data.dark_sidebar_accent_foreground || "",
        dark_sidebar_border: data.dark_sidebar_border || "",
        dark_sidebar_ring: data.dark_sidebar_ring || "",
        // Custom CSS
        custom_css: data.custom_css || "",
      };
      
      form.reset(formData);
    }
  }, [data, form]);

  useImperativeHandle(ref, () => ({
    submitForm: () => onSubmit && form.handleSubmit(handleFormSubmit)(),
  }));

  // Local file name state and refs for the three pickers
  const logoLightInputRef = useRef<HTMLInputElement | null>(null);
  const logoDarkInputRef = useRef<HTMLInputElement | null>(null);
  const sidebarLogoInputRef = useRef<HTMLInputElement | null>(null);
  const faviconInputRef = useRef<HTMLInputElement | null>(null);

  const [logoLightFileName, setLogoLightFileName] = useState<string>("");
  const [logoDarkFileName, setLogoDarkFileName] = useState<string>("");
  const [sidebarLogoFileName, setSidebarLogoFileName] = useState<string>("");
  const [faviconFileName, setFaviconFileName] = useState<string>("");
  const motdEnabled = form.watch("motd_enabled");
  const proTipEnabled = form.watch("pro_tip_enabled");

  const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof BrandingSettingsData, type: 'logo' | 'favicon' | 'sidebar-logo') => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
          const res = await uploadBrandingAssetWithRetry(file, type);

    if (res.error) {
      return;
    }
      form.setValue(fieldName as any, res.url, { shouldDirty: true });
    } catch (error) {
      // Handle upload error silently
    }
  };

  const handleFormSubmit = (values: BrandingSettingsData) => {
    // Transform null values to empty strings for Zod validation
    const transformedValues = {
      ...values,
      logo_light_url: values.logo_light_url || "",
      logo_dark_url: values.logo_dark_url || "",
      sidebar_logo_url: values.sidebar_logo_url || "",
      sidebar_logo_enabled: values.sidebar_logo_enabled ?? true,
      sidebar_logo_size: values.sidebar_logo_size ?? 40,
      favicon_url: values.favicon_url || "",
    };
    
    onSubmit && onSubmit(transformedValues as any);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="flex flex-col lg:flex-row gap-6 min-h-[600px]">
          {/* Left side: Form */}
          <div className="flex-1 min-w-0 flex flex-col">
            <Tabs defaultValue="themes" className="flex-1">
              <TabsList className="w-full justify-start flex flex-wrap">
                <TabsTrigger value="themes">Themes</TabsTrigger>
                <TabsTrigger value="logos">Logos</TabsTrigger>
                <TabsTrigger value="texts">Texts</TabsTrigger>
                <TabsTrigger value="colors">Colors</TabsTrigger>
                <TabsTrigger value="custom-css">Custom CSS</TabsTrigger>
              </TabsList>

          {/* Themes: presets */}
          <TabsContent value="themes" className="space-y-4">
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Theme Presets</div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                {BRANDING_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className="flex items-center justify-start gap-3 rounded-md border p-2 hover:bg-accent h-16 min-h-16"
                    onClick={() => {
                      if (preset.fonts) {
                        if (preset.fonts.font_sans) form.setValue("font_sans" as any, preset.fonts.font_sans, { shouldDirty: true });
                        if (preset.fonts.font_serif) form.setValue("font_serif" as any, preset.fonts.font_serif, { shouldDirty: true });
                        if (preset.fonts.font_mono) form.setValue("font_mono" as any, preset.fonts.font_mono, { shouldDirty: true });
                      }
                      Object.entries(preset.light || {}).forEach(([k, v]) => {
                        form.setValue(k as any, v, { shouldDirty: true });
                      });
                      Object.entries(preset.dark || {}).forEach(([k, v]) => {
                        // Dark theme properties in JSON don't have dark_ prefix, but form expects it
                        const darkKey = k.startsWith("dark_") ? k : `dark_${k}`;
                        form.setValue(darkKey as any, v, { shouldDirty: true });
                      });
                      // Don't auto-submit - user needs to click Save to apply changes
                    }}
                  >
                    <span className="h-6 w-6 rounded" style={{ background: preset.previewColor || "hsl(223 0% 50%)" }} />
                    <div className="flex flex-col text-left flex-1 min-w-0">
                      <span className="text-sm font-medium leading-none truncate">{preset.name}</span>
                      {preset.description && (
                        <span className="text-xs text-muted-foreground line-clamp-2 leading-tight mt-0.5">{preset.description}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Logos */}
          <TabsContent value="logos" className="space-y-4">
            <div className="space-y-4">
              {/* Logo Light Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Logo (Light Theme)</CardTitle>
                  <CardDescription>
                    Logo displayed in light theme mode
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="logo_light_url"
                    render={({ field }) => (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Preview */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-foreground">Preview</h4>
                          <div className="relative w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/10 flex items-center justify-center group">
                            {field.value ? (
                              <>
                                <img 
                                  src={field.value} 
                                  alt="Light Logo Preview" 
                                  className="max-w-full max-h-full object-contain" 
                                />
                                <button
                                  type="button"
                                  onClick={() => field.onChange("")}
                                  className="absolute top-2 right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-sm hover:bg-destructive/90 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                >
                                  ×
                                </button>
                              </>
                            ) : (
                              <div className="text-center">
                                <div className="w-12 h-12 mx-auto mb-2 bg-muted-foreground/10 rounded-lg flex items-center justify-center">
                                  <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <p className="text-sm text-muted-foreground">No logo uploaded</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Upload */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-foreground">Upload</h4>
                          <div className="flex items-center gap-3">
                            <input
                              ref={logoLightInputRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setLogoLightFileName(file.name);
                                handleUploadLogo(e, "logo_light_url", "logo");
                              }}
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              className="flex-shrink-0"
                              onClick={() => logoLightInputRef.current?.click()}
                            >
                              Choose File
                            </Button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-foreground truncate">
                                {logoLightFileName || "No file selected"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                PNG, JPG up to 2MB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Logo Dark Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Logo (Dark Theme)</CardTitle>
                  <CardDescription>
                    Logo displayed in dark theme mode
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="logo_dark_url"
                    render={({ field }) => (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Preview */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-foreground">Preview</h4>
                          <div className="relative w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/10 flex items-center justify-center group">
                            {field.value ? (
                              <>
                                <img 
                                  src={field.value} 
                                  alt="Dark Logo Preview" 
                                  className="max-w-full max-h-full object-contain" 
                                />
                                <button
                                  type="button"
                                  onClick={() => field.onChange("")}
                                  className="absolute top-2 right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-sm hover:bg-destructive/90 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                >
                                  ×
                                </button>
                              </>
                            ) : (
                              <div className="text-center">
                                <div className="w-12 h-12 mx-auto mb-2 bg-muted-foreground/10 rounded-lg flex items-center justify-center">
                                  <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <p className="text-sm text-muted-foreground">No logo uploaded</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Upload */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-foreground">Upload</h4>
                          <div className="flex items-center gap-3">
                            <input
                              ref={logoDarkInputRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setLogoDarkFileName(file.name);
                                handleUploadLogo(e, "logo_dark_url", "logo");
                              }}
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              className="flex-shrink-0"
                              onClick={() => logoDarkInputRef.current?.click()}
                            >
                              Choose File
                            </Button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-foreground truncate">
                                {logoDarkFileName || "No file selected"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                PNG, JPG up to 2MB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </CardContent>
              </Card>
              
              {/* Sidebar Logo Card */}
              <Card className="col-span-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Sidebar Logo</CardTitle>
                      <CardDescription>
                        Logo displayed in the chat sidebar navigation
                      </CardDescription>
                    </div>
                    <FormField
                      control={form.control}
                      name="sidebar_logo_enabled"
                      render={({ field: toggleField }) => (
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium">Enabled</span>
                          <Switch
                            checked={toggleField.value}
                            onCheckedChange={toggleField.onChange}
                          />
                        </div>
                      )}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Preview */}
                    <FormField
                      control={form.control}
                      name="sidebar_logo_url"
                      render={({ field }) => (
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-foreground">Preview</h4>
                          <div className="relative w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/10 flex items-center justify-center group">
                            {field.value ? (
                              <>
                                <img 
                                  src={field.value} 
                                  alt="Sidebar Logo Preview" 
                                  className="max-w-full max-h-full object-contain" 
                                />
                                <button
                                  type="button"
                                  onClick={() => field.onChange("")}
                                  className="absolute top-2 right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-sm hover:bg-destructive/90 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                >
                                  ×
                                </button>
                              </>
                            ) : (
                              <div className="text-center">
                                <div className="w-12 h-12 mx-auto mb-2 bg-muted-foreground/10 rounded-lg flex items-center justify-center">
                                  <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <p className="text-sm text-muted-foreground">No logo uploaded</p>
                                <p className="text-xs text-muted-foreground">Using default logo</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    />

                    {/* Upload and Size Controls */}
                    <div className="space-y-4">
                      {/* Upload Section */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-foreground">Upload</h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <input
                              ref={sidebarLogoInputRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setSidebarLogoFileName(file.name);
                                handleUploadLogo(e, "sidebar_logo_url", "sidebar-logo");
                              }}
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              className="flex-shrink-0"
                              onClick={() => sidebarLogoInputRef.current?.click()}
                            >
                              Choose File
                            </Button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-foreground truncate">
                                {sidebarLogoFileName || "No file selected"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                PNG, JPG up to 2MB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Size Control */}
                      <FormField
                        control={form.control}
                        name="sidebar_logo_size"
                        render={({ field: sizeField }) => (
                          <div className="space-y-3">
                            <h4 className="text-sm font-medium text-foreground">Size</h4>
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Height</span>
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium">{sizeField.value || 40}</span>
                                  <span className="text-xs text-muted-foreground">px</span>
                                </div>
                              </div>
                              <div className="space-y-1">
                                <input
                                  type="range"
                                  min="20"
                                  max="100"
                                  step="5"
                                  value={sizeField.value || 40}
                                  onChange={(e) => sizeField.onChange(parseInt(e.target.value))}
                                  className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer slider"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>20px</span>
                                  <span>100px</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Favicon Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Favicon</CardTitle>
                  <CardDescription>
                    Small icon displayed in browser tabs and bookmarks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="favicon_url"
                    render={({ field }) => (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Preview */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-foreground">Preview</h4>
                          <div className="relative w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/10 flex items-center justify-center group">
                            {field.value ? (
                              <>
                                <img 
                                  src={field.value} 
                                  alt="Favicon Preview" 
                                  className="max-w-full max-h-full object-contain" 
                                />
                                <button
                                  type="button"
                                  onClick={() => field.onChange("")}
                                  className="absolute top-2 right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-sm hover:bg-destructive/90 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                >
                                  ×
                                </button>
                              </>
                            ) : (
                              <div className="text-center">
                                <div className="w-12 h-12 mx-auto mb-2 bg-muted-foreground/10 rounded-lg flex items-center justify-center">
                                  <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <p className="text-sm text-muted-foreground">No favicon uploaded</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Upload */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-foreground">Upload</h4>
                          <div className="flex items-center gap-3">
                            <input
                              ref={faviconInputRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setFaviconFileName(file.name);
                                handleUploadLogo(e, "favicon_url", "favicon");
                              }}
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              className="flex-shrink-0"
                              onClick={() => faviconInputRef.current?.click()}
                            >
                              Choose File
                            </Button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-foreground truncate">
                                {faviconFileName || "No file selected"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                PNG, ICO, SVG up to 2MB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Texts */}
          <TabsContent value="texts" className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* MOTD toggle + input */}
              <div className="space-y-2">
                <FormField
                  control={form.control}
                  name="motd_enabled"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel className="text-sm font-medium">MOTD</FormLabel>
                        <FormControl>
                          <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="motd"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="I'm not just artificially intelligent, I'm artificially enthusiastic too!"
                          {...field}
                          disabled={!motdEnabled}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Pro tip toggle + input */}
              <div className="space-y-2">
                <FormField
                  control={form.control}
                  name="pro_tip_enabled"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel className="text-sm font-medium">Pro tip</FormLabel>
                        <FormControl>
                          <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pro_tip"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder={'Pro tip: Try asking "What can you help me with?" to see available commands'}
                          {...field}
                          disabled={!proTipEnabled}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <FormField
              control={form.control}
              name="app_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>App Title</FormLabel>
                  <FormControl>
                    <Input placeholder="My App" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Fonts */}
            <div className="space-y-4 mt-6">
              <h3 className="text-sm font-medium">Fonts</h3>
              <div className="grid grid-cols-3 gap-4 max-w-xl">
                <FontField name="font_sans" type="sans" />
                <FontField name="font_serif" type="serif" />
                <FontField name="font_mono" type="mono" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="colors" className="space-y-4">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">Light Theme</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {[
                    "primary", "primary_foreground", "secondary", "secondary_foreground", "accent", "accent_foreground",
                    "background", "foreground", "card", "card_foreground", "popover", "popover_foreground",
                    "muted", "muted_foreground", "destructive", "destructive_foreground", "border", "input", "ring",
                    "chart_1", "chart_2", "chart_3", "chart_4", "chart_5",
                    "sidebar", "sidebar_background", "sidebar_foreground", "sidebar_primary", "sidebar_primary_foreground",
                    "sidebar_accent", "sidebar_accent_foreground", "sidebar_border", "sidebar_ring",
                  ].map((name) => (
                    <ColorField key={name} name={name as any} label={name.replace(/_/g, " ")} />
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Dark Theme</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {[
                    "dark_primary", "dark_primary_foreground", "dark_secondary", "dark_secondary_foreground",
                    "dark_accent", "dark_accent_foreground", "dark_background", "dark_foreground",
                    "dark_card", "dark_card_foreground", "dark_popover", "dark_popover_foreground",
                    "dark_muted", "dark_muted_foreground", "dark_destructive", "dark_destructive_foreground",
                    "dark_border", "dark_input", "dark_ring",
                    "dark_chart_1", "dark_chart_2", "dark_chart_3", "dark_chart_4", "dark_chart_5",
                    "dark_sidebar", "dark_sidebar_background", "dark_sidebar_foreground", "dark_sidebar_primary",
                    "dark_sidebar_primary_foreground", "dark_sidebar_accent", "dark_sidebar_accent_foreground",
                    "dark_sidebar_border", "dark_sidebar_ring",
                  ].map((name) => (
                    <ColorField key={name} name={name as any} label={name.replace(/^dark_/, "").replace(/_/g, " ")} />
                  ))}
                </div>
              </div>
              <FormField
                control={form.control}
                name="radius"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>radius</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 0.75rem" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </TabsContent>

          {/* Custom CSS */}
          <TabsContent value="custom-css" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Custom CSS</CardTitle>
                <CardDescription>
                  Add custom CSS rules to further customize the appearance of your chat interface.
                  Your CSS will be injected after the theme styles, allowing you to override any default styling.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="custom_css"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CSS Code</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          placeholder={`/* Example: Custom button styling */
.chat-input button[type="submit"] {
  border-radius: 9999px;
}

/* Example: Hide sidebar on mobile */
@media (max-width: 768px) {
  [data-sidebar] {
    display: none;
  }
}

/* Example: Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}
::-webkit-scrollbar-thumb {
  background: hsl(var(--muted));
  border-radius: 4px;
}`}
                          className="min-h-[400px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          style={{ fontFamily: "var(--font-mono)" }}
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-xs text-muted-foreground mt-2">
                        Maximum 50,000 characters. For available CSS variables and selectors, see the{" "}
                        <a 
                          href="https://junet.io/docs/branding/custom-css" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary underline hover:no-underline"
                        >
                          documentation
                        </a>.
                      </p>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {!hideSubmitButton && (
          <div className="mt-auto pt-6 border-t">
            <Button type="submit">Save</Button>
          </div>
        )}
          </div>

          {/* Right side: Preview */}
          <div className="lg:w-[600px] flex-shrink-0">
            <div className="sticky top-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Live Preview</h3>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant={previewDark ? "ghost" : "secondary"}
                    size="sm"
                    onClick={() => setPreviewDark(false)}
                  >
                    <Sun className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant={previewDark ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setPreviewDark(true)}
                  >
                    <Moon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <ChatPreview 
                values={watchedValues as any} 
                isDark={previewDark}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
});

BrandingSettingsForm.displayName = "BrandingSettingsForm";

export default BrandingSettingsForm;
